<template>
  <div class="cohorts-list-page">
    <AbtestStepper
      :game-id="gameId"
      :version-id="versionId"
      :abtest-id="abtestId"
      :abtest="abtest"
      :in-wip-version="inWipVersion"
      :loading="queryLoading"
    >
      <div class="cohorts-list-page-body">
        <div class="abtest-info">
          <section class="priority">
            <h2>A/B Test Priority</h2>

            <div class="priority-description">
              <span>This A/B Test should have a priority of</span>
              <VdSelect
                placeholder=" "
                :disabled="!isDraftAbtest || totalAllocationRate > 100"
                :model-value="selectedPriority"
                :choices="priorityChoices"
                @update:model-value="onPriorityChanged"
              />
              <VdTooltip class="item" orientation="top">
                <template #content>
                  Priority 1 is the highest priority.
                  <br />
                  If the dropdown is disabled, you may have to change your allocation rate because it's full (100%) or
                  because the randomization won't be reset properly.
                </template>
                <vd-custom-icon name="info-icon" big class="info-icon" />
              </VdTooltip>
            </div>

            <div v-if="abtestsDistribution.length" class="abtests-with-priority">
              <div v-for="ab in abtestsDistribution" :key="ab.id" class="abtest-preview card">
                <div class="color" :style="{ backgroundColor: ab.color }"></div>
                <section>
                  <router-link :to="goToABTestCohortsList(ab.id)" class="label">{{ ab.label }}</router-link>
                  <h3 class="percentage">{{ ab.percentage }} %</h3>
                </section>
              </div>
            </div>

            <p v-if="abtestsDistribution.length" class="allocation-explanation">
              There {{ pluralize("is", abtestsDistribution.length, "are") }} {{ abtestsDistribution.length }} A/B
              {{ pluralize("Test", abtestsDistribution.length) }} with priority {{ selectedPriority }} taking
              <strong>{{ existingABTestAllocationRate }}% of the allocation rate.</strong>
              You can allocate
              <strong>{{ 100 - existingABTestAllocationRate }}% through all your cohorts.</strong>
            </p>

            <div v-if="shouldDisplayAllocationWarning">
              <p>
                There {{ pluralize("is", activeAbtests.length, "are") }} {{ activeAbtests.length }} A/B
                {{ pluralize("Test", activeAbtests.length) }}
                in other priority levels targeting maximum
                <strong>
                  {{ prettifyNumber(100 - availableAllocationForSelectedPriorityWithinThisLayer) }}% of the total
                  population available
                </strong>
                . There are
                <strong>
                  {{ prettifyNumber(availableAllocationForSelectedPriorityWithinThisLayer) }}% users available in the
                  population at this priority level.
                </strong>
                <VdTooltip
                  orientation="right"
                  content="This calculation does not take into account<br />the app version ID or user segment."
                >
                  <VdIcon store="fas" name="fa-triangle-exclamation" color="var(--vd-color-yellow-500)" />
                </VdTooltip>
              </p>

              <a
                target="_blank"
                href="https://www.notion.so/voodoo/AB-Test-on-VoodooTune-113a0b481db4801ebcdaf4716bc9a017?pvs=97#113a0b481db4806f81d2dced870c866b"
              >
                Check how priority works on VT
              </a>
            </div>
          </section>

          <div class="layer-info">
            <VdIcon name="layer-group" store="fa" size="large" />
            <span>{{ layerName }}</span>
          </div>
        </div>

        <section v-if="selectedPriority" v-loading="priorityLoading" class="cohort-distribution">
          <h2>Cohorts Distribution</h2>
          <vd-distribution-bar
            class="distribution-bar"
            :bars="distributionBar"
            :overlap-message="`The sum of the cohorts (${totalAllocationRate}%) exceeds 100%`"
          />
          <CohortsListItem
            v-for="(cohort, index) in cohorts"
            :key="cohort.id"
            class="cohorts-list-item"
            :color="getCohortColor(index)"
            :cohort="cohort"
            :game-id="gameId"
            :version-id="versionId"
            :abtest-id="abtestId"
            :is-draft-abtest="isDraftAbtest"
            :ab-test-type="abTestType"
            :is-dynamic-ab="abtest?.isDynamic"
            :dynamic-segments="dynamicSegments"
            :ab-test-status="abtest!.state"
            :offers-by-id="offersById"
            :campaign-names-by-id="campaignNamesById"
            @delete="onDelete"
            @duplicate="onDuplicate"
            @update-allocation-rate="onUpdateAllocationRate"
          />

          <vd-button
            v-if="isDraftAbtest && !abtest?.isDynamic"
            class="add-cohort-button"
            type="secondary"
            skin="blue"
            icon-left="fa:add"
            @click="onNewCohort"
          >
            Add cohort
          </vd-button>
        </section>

        <!-- If we are on the cohort form, hide the step controls. The form actions will take over -->
        <section class="abtest-actions">
          <vd-button type="secondary" icon-left="fa:chevron-left" @click="onGoBackToListPage">Back to list</vd-button>
          <div v-if="isABTestStepCohortsRoute && isDraftAbtest" class="step-controls">
            <vd-button type="secondary" skin="blue" @click="onGoToPreviousPage">Previous</vd-button>
            <vd-button type="primary" :disabled="!selectedPriority" @click="onNext">Next</vd-button>
          </div>
        </section>

        <!-- This router-view is used for CohortFormPage dialog -->
        <router-view />
      </div>
    </AbtestStepper>
  </div>
</template>

<script setup lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdSelect, VdTooltip } from "@voodoo.io/voodoo-ui";
import { computed, ref, watch } from "vue";
import { RouteLocationRaw, useRoute, useRouter } from "vue-router";

import AbtestStepper from "@/components/AbtestStepper/AbtestStepper.vue";
import { cohortColorsPalette } from "@/constants";
import { pluralize, prettifyNumber } from "@/filters";
import {
  AbtestState,
  queryNames,
  useCohortsListPageDeleteMutation,
  useCohortsListPageQuery,
  useCohortsListPageUpdateCohortsAllocationRatesMutation,
  useCohortsListPageUpdatePriorityMutation,
  useMutationResponse,
} from "@/graphql";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useMessages from "@/hooks/messages";
import useTreatmentOffersData from "@/modules/Offers/hooks/useTreatmentOffersData";
import links from "@/router/links";

import CohortsListItem from "./components/CohortsListItem.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  abtestId: {
    type: String,
    required: true,
  },
  /**
   * If true, force the classes reloading.
   * This is used when importing a class from the catalog,
   * forcing the reload will update the classes list after import.
   */
  forceReload: {
    type: String,
    default: null,
  },
});

const router = useRouter();
const route = useRoute();
const { alert, confirm } = useMessages();

// Offer/campaign lookups for the cohort treatments display, fetched once here instead of per treatment.
const { offersById, campaignNamesById } = useTreatmentOffersData(props.gameId, props.versionId);

/**
 * If we have a abtestId in props then fetch the abtest.
 */
const {
  result,
  loading: queryLoading,
  restart,
} = useCohortsListPageQuery(
  {
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtestId,
  },
  { fetchPolicy: "network-only" }
);

/**
 *  This watch is required as we are in router-view
 * see vue-apollo issue: https://github.com/vuejs/vue-apollo/issues/1210
 */
watch(
  () => props.forceReload,
  (reloadRequired) => {
    if (reloadRequired === "true") {
      restart();
    }
  }
);

const inWipVersion = computed(() => result.value?.game?.version?.isWip ?? false);

const abtest = computed(() => result.value?.game?.version?.currentAbtest ?? undefined);

const currentLayer = computed(() => abtest.value?.layerId ?? undefined);
const layerName = computed(() => abtest.value?.layer?.name ?? "");
const dynamicSegments = computed(() => abtest.value?.dynamicSegments ?? []);

const activeAbtests = computed(
  () =>
    result.value?.game?.version?.abtests.filter(
      ({ state, layerId }) =>
        state !== AbtestState.Terminating && state !== AbtestState.Archived && layerId === currentLayer.value
    ) ?? []
);

const otherPublishedAbtestsPriorities = computed(() => activeAbtests.value.map(({ priority }) => priority));

const isDraftAbtest = computed(() => (abtest?.value?.state ? abtest.value?.state === AbtestState.Draft : true));
// TODO: on https://voodoo.atlassian.net/browse/GT-1248, handle the isDynamic case
const abTestType = computed(() => abtest.value?.treatmentTypes?.[0] ?? "class");
/**
 * Priority field
 */
const selectedPriority = ref();

/**
 * Take all existing abtest priorities + add one more (increment the last priority)
 * so we can add the abtest into a new priority (instead of existing ones)
 */
const priorityChoices = computed(() => {
  const lastPriority = activeAbtests.value.reduce((acc, a) => (acc > a.priority ? acc : a.priority), 0);
  /**
   * generate an array of size "higher prio + 2" and extract an incremental array from its keys
   */
  const availablePriorities = [...Array(lastPriority + 2).keys()];
  return availablePriorities.map((priority) => ({
    label: `${priority + 1}`,
    value: priority + 1,
    disabled: otherPublishedAbtestsPriorities.value.includes(priority + 1),
  }));
});

const sortedABTestsByPriority = computed(() => {
  let copyOfAbtests: typeof activeAbtests.value = [];
  if (activeAbtests.value) {
    copyOfAbtests = [...activeAbtests.value];
  }
  return copyOfAbtests.sort((firstAb, secondAb) => firstAb.priority - secondAb.priority);
});

const isHighestPriorityWithinThisLayer = computed(
  () => sortedABTestsByPriority.value.length > 0 && selectedPriority.value === sortedABTestsByPriority.value[0].priority
);

const shouldDisplayAllocationWarning = computed(
  () =>
    activeAbtests.value.length > 1 && !isHighestPriorityWithinThisLayer.value && selectedPriority.value !== undefined
);
const availableAllocationForSelectedPriorityWithinThisLayer = computed(() => {
  const availableSeatsPerPriority: number[] = [];
  sortedABTestsByPriority.value
    ?.filter((ab) => ab.priority < (selectedPriority.value ?? 0))
    .forEach((ab, index) => {
      availableSeatsPerPriority[index] =
        1 - ab.cohorts.reduce((acc, { allocationRate }) => acc + allocationRate, 0) / 100;
    });
  return availableSeatsPerPriority.length > 0
    ? availableSeatsPerPriority.reduce((acc, allocationRate) => acc * allocationRate, 100)
    : 0;
});

const { mutate: updateABTestPriority, loading: priorityLoading } = useCohortsListPageUpdatePriorityMutation({
  refetchQueries: [queryNames.CohortsListPage],
});

const onPriorityChanged = async (priority: number) => {
  if (selectedPriority.value !== priority) {
    selectedPriority.value = priority;
    const response = await updateABTestPriority({
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
      priority,
    });

    useMutationResponse(response, (resp) => resp?.updateAbtestPriority);
  }
};

const abtestsDistribution = computed(() => {
  const samePriorityABTests = activeAbtests.value.filter(
    ({ id, priority }) => id !== props.abtestId && priority === selectedPriority.value
  );
  return samePriorityABTests.map(({ id, name, cohorts }, index) => ({
    id,
    label: name,
    percentage: cohorts.reduce((acc, { allocationRate }) => acc + allocationRate, 0),
    color: cohortColorsPalette[(7 + index * 5) % cohortColorsPalette.length],
  }));
});

const existingABTestAllocationRate = computed(() =>
  abtestsDistribution.value.reduce((acc, { percentage }) => acc + percentage, 0)
);

watch(abtest, (watchedAbtest) => {
  selectedPriority.value = watchedAbtest?.priority || undefined;
});

const cohorts = computed(() => abtest.value?.cohorts ?? []);
const controlCohortRate = computed(() => abtest.value?.cohorts.find((c) => c.name === "Control")?.allocationRate);

const cohortsDistribution = ref<
  {
    id: string;
    label: string;
    percentage: number;
    color: string;
  }[]
>([]);

const getCohortColor = (index: number) => cohortColorsPalette[(index * 5) % cohortColorsPalette.length];

const totalAllocationRate = computed(
  () =>
    existingABTestAllocationRate.value + cohortsDistribution.value.reduce((acc, { percentage }) => acc + percentage, 0)
);

/**
 * If the cohorts changes, update the distribution bar.
 */
watch(cohorts, (watchedCohorts) => {
  cohortsDistribution.value = watchedCohorts.map(({ id, name, allocationRate }, index) => ({
    id,
    label: name,
    percentage: allocationRate,
    color: cohortColorsPalette[(index * 5) % cohortColorsPalette.length],
  }));
});

/**
 *
 * Update the allocation rates
 *
 */
const { mutate: updateAllocationRates } = useCohortsListPageUpdateCohortsAllocationRatesMutation({
  refetchQueries: [queryNames.CohortsListPage],
});

/**
 * If the total of the allocation rates are ok (ie: <=100), then
 * send the allocation rates to the server and reload the cohorts.
 */
const saveAllocationRates = async () => {
  const totalPercentage = cohortsDistribution.value.reduce((acc, { percentage }) => acc + percentage, 0);
  const input = {
    allocations: cohortsDistribution.value.map(({ id, percentage }) => ({
      cohortId: id,
      allocationRate: percentage,
    })),
  };

  if (totalPercentage <= 100) {
    const response = await updateAllocationRates({
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
      input,
    });
    useMutationResponse(response, (resp) => resp?.updateCohortsAllocationRates);
  }
};

const onUpdateAllocationRate = async ({ cohortId, allocationRate }: { cohortId: string; allocationRate: number }) => {
  /**
   * Update the cohorts distribution list with the new allocation rate
   * to update the distribution bar.
   */
  cohortsDistribution.value = cohortsDistribution.value.map((item) => {
    if (item.id === cohortId) {
      return { ...item, percentage: allocationRate };
    }
    return item;
  });

  /**
   * Send allocation rates to the server
   */
  saveAllocationRates();
};

/**
 * Delete a cohort
 */
const { flags } = useFeatureFlags(props.gameId);
const { mutate: deleteCohort } = useCohortsListPageDeleteMutation({ refetchQueries: [queryNames.CohortsListPage] });

const onDelete = async (cohortId: string) => {
  const confirmed = await confirm("Do you really want to delete this cohort?");
  if (confirmed) {
    const response = await deleteCohort({
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
      cohortId,
      useMultiLink: flags.value.offerMultiLinkABTest.enabled,
    });

    useMutationResponse(response, (resp) => resp?.deleteCohort);
  }
};

const onDuplicate = async (cohortId: string) => {
  router.push({
    name: links.games.abtests.cohorts.duplicate,
    params: {
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
      cohortId,
    },
  });
};

/**
 * Check distribution and move to the review page
 */

const onNext = async () => {
  let confirmed = false;
  if (controlCohortRate.value === 0) {
    confirmed = (await confirm(
      "Your control cohort has a 0% of allocation rate. Your A/B test will not appear in the analysis dashboard. Are you sure you want to continue?",
      {
        title: "Warning",
        type: "warning",
        confirmButtonText: "I understand",
      }
    )) as boolean;
  } else {
    confirmed = true;
  }

  if (confirmed) {
    router.push({
      name: links.games.abtests.review,
      params: {
        gameId: props.gameId,
        versionId: props.versionId,
        abtestId: props.abtestId,
      },
    });
  }

  if (totalAllocationRate.value > 100) {
    alert(`The sum of the cohorts (${totalAllocationRate.value}%) exceeds 100%`, {
      title: "Warning",
      type: "warning",
      confirmButtonText: "I understand",
    });
  } else {
    router.push({
      name: links.games.abtests.review,
      params: {
        gameId: props.gameId,
        versionId: props.versionId,
        abtestId: props.abtestId,
      },
    });
  }
};

/**
 * Routing
 */

const onNewCohort = () => {
  router.push({
    name: links.games.abtests.cohorts.new,
    params: {
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
    },
  });
};

const onGoBackToListPage = async () => {
  let confirmed = false;
  if (controlCohortRate.value === 0) {
    confirmed = (await confirm(
      "Your control cohort has a 0% of allocation rate. Your A/B test will not appear in the analysis dashboard. Are you sure you want to continue?",
      {
        title: "Warning",
        type: "warning",
        confirmButtonText: "I understand",
      }
    )) as boolean;
  } else {
    confirmed = true;
  }

  if (confirmed) {
    router.push({
      name: links.games.abtests.list,
    });
  }
};

const onGoToPreviousPage = async () => {
  let confirmed = false;
  if (controlCohortRate.value === 0) {
    confirmed = (await confirm(
      "Your control cohort has a 0% of allocation rate. Your A/B test will not appear in the analysis dashboard. Are you sure you want to continue?",
      {
        title: "Warning",
        type: "warning",
        confirmButtonText: "I understand",
      }
    )) as boolean;
  } else {
    confirmed = true;
  }

  if (confirmed) {
    router.push({
      name: abtest.value?.isDynamic ? links.games.abtests.dynamicTreatment : links.games.abtests.edit,
      params: {
        gameId: props.gameId,
        versionId: props.versionId,
        abtestId: props.abtestId,
      },
    });
  }
};

const isABTestStepCohortsRoute = computed(() => route.name === links.games.abtests.cohorts.list);

const goToABTestCohortsList = (abtestId: string): RouteLocationRaw => ({
  name: links.games.abtests.cohorts.list,
  params: { gameId: props.gameId, versionId: props.versionId, abtestId },
});
/**
 * Distribution bar values
 */
const distributionBar = computed(() => [
  ...abtestsDistribution.value.map((item) => ({ ...item, label: `A/B Test ${item.label}` })),
  ...cohortsDistribution.value,
]);
</script>

<style lang="scss" scoped>
.cohorts-list-page-body {
  .abtest-info {
    display: grid;
    grid-template-columns: 1fr auto;
  }

  section > h2 {
    margin-bottom: size("M");
  }

  .layer-info {
    display: inline-flex;
    height: 16px;
    align-items: center;
    column-gap: size("S");
    color: $grey;

    svg {
      color: $grey;
    }
  }

  .priority-description {
    display: flex;
    align-items: center;
    column-gap: size("S");

    span {
      font-weight: 600;
    }

    .vd-select {
      width: 60px;
    }

    .info-icon {
      fill: $light-blue;
    }
  }

  .priority {
    .abtests-with-priority {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      margin-top: size("M");

      .abtest-preview {
        display: inline-flex;
        align-items: center;
        @extend %card;
        padding: size("ML");
        margin-right: size("ML");
        margin-bottom: size("M");
        min-width: 200px;

        .color {
          height: 40px;
          width: 4px;
          border-radius: 10px;
          margin-right: size("ML");
        }

        .percentage {
          font-weight: 600;
        }

        .label {
          margin-bottom: size("XS");
        }
      }
    }
  }

  .cohort-distribution {
    margin-top: size("XXL");
  }

  .distribution-bar {
    margin: size("L") 0 size("XL");
  }

  .cohorts-list-item {
    margin-bottom: size("L");
  }

  .abtest-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: size("XL");

    .step-controls {
      display: flex;
      align-items: center;

      .vd-button {
        margin-left: size("M");
      }
    }
  }
}
</style>
